import { useEffect, useState } from 'react';

/**
 * Say when this page is older than the server it is talking to.
 *
 * Written after "Voice cache — menu not showing". The route, the nav entry and
 * the page all existed and the built bundle contained them; the browser was
 * running an older bundle. Nothing anywhere said so — every other page worked
 * and the new one was simply absent, which is indistinguishable from a feature
 * that was never built.
 *
 * Two versions are compared: the release this bundle was built from, baked in
 * by Vite, and the one `/health` reports. `/health` needs no authentication
 * and is deliberately cheap, so this costs one request per page load.
 *
 * It reports rather than acts. Reloading the page automatically would be a
 * loop if the cause is a build that never happened — which is the other half
 * of why this appears, and the half a reload cannot fix.
 */

const DISMISSED_KEY = 'glee.stale-ui-dismissed';

export function StaleUiNotice() {
  const [serverVersion, setServerVersion] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(DISMISSED_KEY) === 'yes',
  );

  useEffect(() => {
    let cancelled = false;
    fetch('/health', { headers: { accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : null))
      .then((body) => {
        if (!cancelled && body?.version) setServerVersion(String(body.version));
      })
      .catch(() => {
        // A health check that cannot be reached says nothing about staleness.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const uiVersion = typeof __UI_VERSION__ === 'string' ? __UI_VERSION__ : 'unknown';
  const stale =
    serverVersion !== null &&
    uiVersion !== 'unknown' &&
    serverVersion !== uiVersion;

  if (!stale || dismissed) return null;

  return (
    <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100">
      <p className="font-medium">
        This page is from {uiVersion}; the server is running {serverVersion}.
      </p>
      <p className="mt-1 text-xs">
        Anything added since {uiVersion} will be missing from the menu. Reload
        the page first. If it says the same thing afterwards the browser assets
        were never rebuilt — on the server, run{' '}
        <code className="font-mono">glee-doctor</code>, which checks exactly
        this, and re-run the upgrade.
      </p>
      <button
        type="button"
        className="mt-2 text-xs underline"
        onClick={() => {
          sessionStorage.setItem(DISMISSED_KEY, 'yes');
          setDismissed(true);
        }}
      >
        Dismiss for this session
      </button>
    </div>
  );
}
